import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  MenuItem,
  Grid,
  Slider,
  FormHelperText,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import Select from '../UIKit/Select';
import TextField from '../UIKit/TextField';
import Card from '../UIKit/Card';
import { useTranslation } from 'react-i18next';

const CriteriaCondition = ({
  condition,
  onChange,
  onTypeChange,
  onRemove,
  showRemoveButton,
  availableCourses,
  availableAchievements,
  availableTests,
  availableChallenges,
  loadingOptions,
}) => {
  const { t } = useTranslation(['badges']);
  
  if (!condition) return null;

  // Простая функция для обработки изменения параметров
  const handleParamChange = (paramKey, value) => {
    onChange(paramKey, value);
  };

  // Функция для определения текущего выбранного параметра курса
  const getCourseParamType = () => {
    if (condition.courseIds) return 'multiple';
    if (condition.courseId !== undefined) return 'single';
    return 'count';
  };

  // Функция для изменения типа параметра курса через прямое изменение condition
  const handleCourseParamTypeChange = (newType) => {
    // Создаем новый объект condition с базовыми параметрами
    const newCondition = {
      ...condition,
      courseId: undefined,
      courseIds: undefined,
      courseCount: undefined,
    };

    // Устанавливаем нужные параметры в зависимости от типа
    if (newType === 'single') {
      newCondition.courseId = '';
    } else if (newType === 'multiple') {
      newCondition.courseIds = [];
    } else if (newType === 'count') {
      newCondition.courseCount = 1;
    }

    // Обновляем каждый параметр через функцию onChange
    Object.keys(newCondition).forEach((key) => {
      if (key !== 'type' && newCondition[key] !== condition[key]) {
        onChange(key, newCondition[key]);
      }
    });
  };

  // Рендер конкретного типа условия
  const renderConditionFields = () => {
    switch (condition.type) {
      case 'courseCompletion':
        const paramType = getCourseParamType();

        return (
          <>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <Select
                value={paramType}
                onChange={(e) => handleCourseParamTypeChange(e.target.value)}
                label={t('badges:criteriaCondition.courseParam')}
              >
                <MenuItem value="single">
                  {t('badges:criteriaCondition.singleCourse')}
                </MenuItem>
                <MenuItem value="multiple">
                  {t('badges:criteriaCondition.multipleCourses')}
                </MenuItem>
                <MenuItem value="count">
                  {t('badges:criteriaCondition.courseCount')}
                </MenuItem>
              </Select>
            </FormControl>

            {paramType === 'single' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  value={condition.courseId || ''}
                  onChange={(e) =>
                    handleParamChange('courseId', e.target.value)
                  }
                  label={t('badges:criteriaCondition.selectCourse')}
                  disabled={loadingOptions}
                >
                  {loadingOptions ? (
                    <MenuItem value="">
                      {t('badges:criteriaCondition.loadingCourses')}
                    </MenuItem>
                  ) : Array.isArray(availableCourses) &&
                    availableCourses.length > 0 ? (
                    availableCourses.map((course) => (
                      <MenuItem key={course._id} value={course._id}>
                        {course.title}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      {t('badges:criteriaCondition.noCoursesAvailable')}
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText>
                  {Array.isArray(availableCourses) &&
                  availableCourses.length === 0
                    ? t('badges:criteriaCondition.createCoursesFirst')
                    : t('badges:criteriaCondition.selectCourseToComplete')}
                </FormHelperText>
              </FormControl>
            )}

            {paramType === 'multiple' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  multiple
                  value={condition.courseIds || []}
                  onChange={(e) =>
                    handleParamChange('courseIds', e.target.value)
                  }
                  label={t('badges:criteriaCondition.selectCourses')}
                  disabled={loadingOptions}
                  renderValue={(selected) => {
                    if (loadingOptions)
                      return t('badges:criteriaCondition.loadingCourses');
                    if (
                      !Array.isArray(selected) ||
                      !Array.isArray(availableCourses)
                    )
                      return t('badges:criteriaCondition.noCoursesSelected');
                    return selected
                      .map((id) => {
                        const course = availableCourses.find(
                          (c) => c && c._id === id
                        );
                        return course ? course.title : id;
                      })
                      .join(', ');
                  }}
                >
                  {loadingOptions ? (
                    <MenuItem value="">
                      {t('badges:criteriaCondition.loadingCourses')}
                    </MenuItem>
                  ) : Array.isArray(availableCourses) &&
                    availableCourses.length > 0 ? (
                    availableCourses.map((course) => (
                      <MenuItem key={course._id} value={course._id}>
                        {course.title}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      {t('badges:criteriaCondition.noCoursesAvailable')}
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText>
                  {Array.isArray(availableCourses) &&
                  availableCourses.length === 0
                    ? t('badges:criteriaCondition.createCoursesFirst')
                    : t('badges:criteriaCondition.selectCoursesToComplete')}
                </FormHelperText>
              </FormControl>
            )}

            {paramType === 'count' && (
              <TextField
                label={t('badges:criteriaCondition.courseCount')}
                fullWidth
                type="number"
                value={condition.courseCount || 1}
                onChange={(e) =>
                  handleParamChange('courseCount', parseInt(e.target.value))
                }
                InputProps={{ inputProps: { min: 1 } }}
                helperText={t('badges:criteriaCondition.courseCountHelp')}
              />
            )}
          </>
        );

      case 'achievementCount':
        return (
          <TextField
            label={t('badges:criteriaCondition.achievementCount')}
            fullWidth
            type="number"
            value={condition.count || 1}
            onChange={(e) =>
              handleParamChange('count', parseInt(e.target.value))
            }
            InputProps={{ inputProps: { min: 1 } }}
            helperText={t('badges:criteriaCondition.achievementCountHelp')}
          />
        );

      case 'specificAchievements':
        return (
          <FormControl fullWidth>
            <Select
              multiple
              value={condition.achievementIds || []}
              onChange={(e) =>
                handleParamChange('achievementIds', e.target.value)
              }
              label={t('badges:criteriaCondition.selectAchievements')}
              disabled={loadingOptions}
              renderValue={(selected) => {
                if (loadingOptions)
                  return t('badges:criteriaCondition.loadingAchievements');
                return selected
                  .map((id) => {
                    const achievement = availableAchievements.find(
                      (a) => a._id === id
                    );
                    return achievement ? achievement.title : id;
                  })
                  .join(', ');
              }}
            >
              {loadingOptions ? (
                <MenuItem value="">
                  {t('badges:criteriaCondition.loadingAchievements')}
                </MenuItem>
              ) : (
                availableAchievements.map((achievement) => (
                  <MenuItem key={achievement._id} value={achievement._id}>
                    {achievement.title}
                  </MenuItem>
                ))
              )}
            </Select>
            <FormHelperText>
              {t('badges:criteriaCondition.selectAchievementsHelp')}
            </FormHelperText>
          </FormControl>
        );

      case 'forumActivity':
        return (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label={t('badges:criteriaCondition.topicCount')}
                fullWidth
                type="number"
                value={condition.topicCount || 1}
                onChange={(e) =>
                  handleParamChange('topicCount', parseInt(e.target.value))
                }
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t('badges:criteriaCondition.postCount')}
                fullWidth
                type="number"
                value={condition.postCount || 5}
                onChange={(e) =>
                  handleParamChange('postCount', parseInt(e.target.value))
                }
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
          </Grid>
        );

      case 'streak':
        return (
          <Box sx={{ mb: 2 }}>
            <Typography gutterBottom>
              {t('badges:criteriaCondition.minStreak')}: {condition.minStreak || 3}{' '}
              {t('badges:criteriaCondition.days')}
            </Typography>
            <Slider
              value={condition.minStreak || 3}
              onChange={(e, value) => handleParamChange('minStreak', value)}
              step={1}
              marks
              min={1}
              max={30}
              valueLabelDisplay="auto"
            />
          </Box>
        );

      case 'testCompletion':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                multiple
                value={condition.testIds || []}
                onChange={(e) => handleParamChange('testIds', e.target.value)}
                label={t('badges:criteriaCondition.selectTests')}
                disabled={loadingOptions}
                renderValue={(selected) => {
                  if (loadingOptions)
                    return t('badges:criteriaCondition.loadingTests');
                  return selected
                    .map((id) => {
                      const test = availableTests.find((t) => t._id === id);
                      return test ? test.title : id;
                    })
                    .join(', ');
                }}
              >
                {loadingOptions ? (
                  <MenuItem value="">
                    {t('badges:criteriaCondition.loadingTests')}
                  </MenuItem>
                ) : (
                  availableTests.map((test) => (
                    <MenuItem key={test._id} value={test._id}>
                      {test.title}
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>
                {t('badges:criteriaCondition.selectTestsHelp')}
              </FormHelperText>
            </FormControl>
            <Box sx={{ mb: 2 }}>
              <Typography gutterBottom>
                {t('badges:criteriaCondition.minScore')}: {condition.minScore || 70}%
              </Typography>
              <Slider
                value={condition.minScore || 70}
                onChange={(e, value) => handleParamChange('minScore', value)}
                step={5}
                marks
                min={50}
                max={100}
                valueLabelDisplay="auto"
              />
            </Box>
          </>
        );

      case 'seasonalExperience':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={condition.season || 'season1'}
                onChange={(e) => handleParamChange('season', e.target.value)}
                label={t('badges:criteriaCondition.season')}
              >
                <MenuItem value="season1">
                  {t('badges:criteriaCondition.season1')}
                </MenuItem>
                <MenuItem value="season2">
                  {t('badges:criteriaCondition.season2')}
                </MenuItem>
                <MenuItem value="season3">
                  {t('badges:criteriaCondition.season3')}
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('badges:criteriaCondition.requiredExp')}
              fullWidth
              type="number"
              value={condition.requiredExp || 100}
              onChange={(e) =>
                handleParamChange('requiredExp', parseInt(e.target.value))
              }
              InputProps={{ inputProps: { min: 1 } }}
              helperText={t('badges:criteriaCondition.requiredExpHelp')}
            />
          </>
        );

      case 'challengeCompletion':
        return (
          <FormControl fullWidth>
            <Select
              value={condition.challengeId || ''}
              onChange={(e) => handleParamChange('challengeId', e.target.value)}
              label={t('badges:criteriaCondition.selectChallenge')}
              disabled={loadingOptions}
            >
              {loadingOptions ? (
                <MenuItem value="">
                  {t('badges:criteriaCondition.loadingChallenges')}
                </MenuItem>
              ) : (
                availableChallenges.map((challenge) => (
                  <MenuItem key={challenge._id} value={challenge._id}>
                    {challenge.title}
                  </MenuItem>
                ))
              )}
            </Select>
            <FormHelperText>
              {t('badges:criteriaCondition.selectChallengeHelp')}
            </FormHelperText>
          </FormControl>
        );

      case 'userLevel':
        return (
          <Box sx={{ mb: 2 }}>
            <Typography gutterBottom>
              {t('badges:criteriaCondition.minLevel')}: {condition.level || 5}
            </Typography>
            <Slider
              value={condition.level || 5}
              onChange={(e, value) => handleParamChange('level', value)}
              step={1}
              marks
              min={1}
              max={50}
              valueLabelDisplay="auto"
            />
          </Box>
        );

      case 'totalExperience':
        return (
          <TextField
            label={t('badges:criteriaCondition.userExperience')}
            fullWidth
            type="number"
            value={condition.experience || 1000}
            onChange={(e) =>
              handleParamChange('experience', parseInt(e.target.value))
            }
            InputProps={{ inputProps: { min: 1 } }}
            helperText={t('badges:criteriaCondition.userExperienceHelp')}
          />
        );

      case 'courseCategoryCompletion':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={condition.category || 'programming'}
                onChange={(e) => handleParamChange('category', e.target.value)}
                label={t('badges:criteriaCondition.courseCategory')}
              >
                <MenuItem value="programming">
                  {t('badges:criteriaCondition.categories.programming')}
                </MenuItem>
                <MenuItem value="design">
                  {t('badges:criteriaCondition.categories.design')}
                </MenuItem>
                <MenuItem value="marketing">
                  {t('badges:criteriaCondition.categories.marketing')}
                </MenuItem>
                <MenuItem value="management">
                  {t('badges:criteriaCondition.categories.management')}
                </MenuItem>
                <MenuItem value="analytics">
                  {t('badges:criteriaCondition.categories.analytics')}
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('badges:criteriaCondition.courseCount')}
              fullWidth
              type="number"
              value={condition.count || 1}
              onChange={(e) =>
                handleParamChange('count', parseInt(e.target.value))
              }
              InputProps={{ inputProps: { min: 1 } }}
              helperText={t('badges:criteriaCondition.categoryCoursesHelp')}
            />
          </>
        );

      default:
        return (
          <Typography color="text.secondary">
            {t('badges:criteriaCondition.noParamsRequired')}
          </Typography>
        );
    }
  };

  return (
    <Card sx={{ p: 2, mb: 2, position: 'relative' }}>
      {showRemoveButton && (
        <IconButton
          size="small"
          color="error"
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
          onClick={onRemove}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}

      <FormControl fullWidth sx={{ mb: 2 }}>
        <Select
          value={condition.type || 'manual'}
          onChange={(e) => onTypeChange(e.target.value)}
          label={t('badges:criteriaCondition.conditionType')}
        >
          <MenuItem value="courseCompletion">
            {t('badges:criteriaCondition.types.courseCompletion')}
          </MenuItem>
          <MenuItem value="achievementCount">
            {t('badges:criteriaCondition.types.achievementCount')}
          </MenuItem>
          <MenuItem value="specificAchievements">
            {t('badges:criteriaCondition.types.specificAchievements')}
          </MenuItem>
          <MenuItem value="forumActivity">
            {t('badges:criteriaCondition.types.forumActivity')}
          </MenuItem>
          <MenuItem value="streak">
            {t('badges:criteriaCondition.types.streak')}
          </MenuItem>
          <MenuItem value="testCompletion">
            {t('badges:criteriaCondition.types.testCompletion')}
          </MenuItem>
          <MenuItem value="seasonalExperience">
            {t('badges:criteriaCondition.types.seasonalExperience')}
          </MenuItem>
          <MenuItem value="challengeCompletion">
            {t('badges:criteriaCondition.types.challengeCompletion')}
          </MenuItem>
          <MenuItem value="userLevel">
            {t('badges:criteriaCondition.types.userLevel')}
          </MenuItem>
          <MenuItem value="totalExperience">
            {t('badges:criteriaCondition.types.totalExperience')}
          </MenuItem>
          <MenuItem value="courseCategoryCompletion">
            {t('badges:criteriaCondition.types.courseCategoryCompletion')}
          </MenuItem>
          <MenuItem value="manual">
            {t('badges:criteriaCondition.types.manual')}
          </MenuItem>
        </Select>
      </FormControl>

      {renderConditionFields()}
    </Card>
  );
};

export default CriteriaCondition;
